import type {FC, ReactElement} from 'react'
import {lazy, StrictMode, Suspense, useEffect} from 'react'
import {useMediaQuery} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import {Navigate, Route, Routes, useLocation, useParams, useSearchParams} from 'react-router-dom'
import {isNewAuthSystemEnabled} from '@apis/auth'
import type {AuthContextProps} from '@containers/App/AuthProvider'
import {useAuthContext} from '@containers/App/AuthProvider'
import {useUserContext} from '@containers/App/UserProvider'
import {reshapeLegacyToExplorer} from '@shared/utils/legacySignalsRedirect'
import AnomalyComponent from '@containers/Dashboards/Anomalies/index.tsx'
import LogbookEntryComponent from '@containers/Dashboards/Logbook/index.tsx'
import ExternalBookmarkComponent from '@containers/Dashboards/ExternalBookmarks/index.tsx'
import AuthRedirect from '@shared/components/auth/AuthRedirect.tsx'
import AuthTemplate from '@shared/templates/AuthTemplate'
import AuthTemplateLoader from '@shared/components/loaders/dashboards/AuthTemplate'
import BatchTraceabilityLoader from '@shared/components/loaders/dashboards/BatchTraceability'
import TasksLoader from '@shared/components/loaders/dashboards/Tasks'
import DashboardManagementLoader from '@shared/components/loaders/dashboards/DashboardManagement'
import DashTemplateLoader from '@shared/components/loaders/dashboards/DashTemplate'
import LineDashboardLoader from '@shared/components/loaders/dashboards/LineDashboard'
import LinesOverviewLoader from '@shared/components/loaders/dashboards/LinesOverview'
import MinimizedTemplateLoader from '@shared/components/loaders/dashboards/MinimizedTemplate'
import SettingsLoader from '@shared/components/loaders/dashboards/Settings'
import BirdieInitializer from '@shared/components/birdie/BirdieInitializer'
import BirdieRecorderPage from '@shared/components/birdie/BirdieRecorderPage'
import GlobalUsageTracker from '@shared/components/usage/GlobalUsageTracker'
import {TimeIntervalProvider} from '@shared/providers/TimeIntervalProvider.tsx'
import DashTemplate from '@shared/templates/DashTemplate'
import MinimizedTemplate from '@shared/templates/MinimizedTemplate'
import BasicDashboardContainer from '@app/shared/components/BasicDashboardContainer.tsx'
import SignalValueTrackersTable from '@containers/Dashboards/SignalValueTrackers'
import {INITIAL_TIME_RANGE_ONE_DAY} from '@app/shared/constants/common.ts'
import AgentChat from '@app/containers/Dashboards/AgentChat'
import EdgeViewCleanup from '@containers/App/EdgeViewCleanup'
import {getPreferredHomeRoute} from '@shared/utils/homeRoutes'
import {useV2App} from '@shared/hooks/useV2App'
import {
  DASHBOARD_PRINT_ROUTE,
  DISPLAY_DASHBOARD_ROUTE,
  DISPLAY_PAIR_ROUTE,
  DISPLAY_ROUTE,
  DISPLAY_SHORT_ROUTE,
  isDashboardPrintPath,
  isDisplayPairPath,
  isDisplayPath,
} from '@shared/utils/printRoute'
import {hideBoot} from '@v2/kit/brand/bootLoader'
import {readDisplayCredential} from '@shared/display/displayCredential'

/** Pages matched ahead of the shells (Routes below): the new shell never ends the boot screen there. */
const OUTSIDE_SHELL_PATHS = [
  '/authorize-callback',
  '/authentication/code/callback',
  '/alpinist/sharepoint-callback',
  '/recorder',
]

// The new experience's app (AB#6639), loaded only for users who see it.
const V2App = lazy(() => import('@v2/app/V2App').then((module) => ({default: module.V2App})))
const DisplayPairingScreen = lazy(() => import('@containers/Display/DisplayPairingScreen'))
const DisplayDashboardView = lazy(() => import('@containers/Display/DisplayDashboardView'))
const DisplayPairPage = lazy(() => import('@containers/Display/DisplayPairPage'))
const AuthCodeCallback = lazy(() => import('@shared/components/auth/AuthCodeCallback'))
const SharePointEntraCallback = lazy(
  () => import('@shared/components/alpinist/SharePointEntraCallback'),
)
// Lazily load components
const LoginForm = lazy(() => import('../../shared/components/auth/forms/LoginForm'))
const RegisterForm = lazy(() => import('../../shared/components/auth/forms/RegisterForm'))
const Home = lazy(() => import('../Dashboards/Home'))
const LinesOverview = lazy(() => import('../Dashboards/LinesOverview'))
const LineDashboard = lazy(() => import('../Dashboards/LineDashboard'))
const CustomDashboards = lazy(() => import('../Dashboards/CustomDashboards'))
const CustomDashboardView = lazy(() => import('../Dashboards/CustomDashboards/DashboardView'))
const DashboardPrintView = lazy(
  () => import('../Dashboards/CustomDashboards/print/DashboardPrintView'),
)
// const LineManagement = lazy(() => import('../Dashboards/LineManagement'))
const LineMaintenance = lazy(() => import('../Dashboards/LineMaintenance'))
const Settings = lazy(() => import('../Dashboards/Settings/Settings'))
const InactiveUser = lazy(() => import('../../shared/pages/InactiveUser'))
const BatchTraceability = lazy(() => import('../Dashboards/BatchTraceability'))
const Tasks = lazy(() => import('../Dashboards/Tasks'))
const Reports = lazy(() => import('../Dashboards/Reports'))
const SignalsWorkspace = lazy(() => import('../SignalsWorkspace/SignalsWorkspace'))

const SignalsDashboard = lazy(() => import('../Dashboards/SignalsDashboard'))
const NoPage = lazy(() => import('../../shared/pages/NoPage'))
const ComparePDFs = lazy(() => import('../../shared/pages/ComparePDFs'))
const DesktopOnly = lazy(() => import('../../shared/pages/DesktopOnly'))
const StartupKitOnboarding = lazy(() => import('../../shared/pages/StartupKitOnboarding'))
const Welcome = lazy(() => import('../../shared/pages/Welcome'))
const Cockpit = lazy(() => import('../../shared/components/maps/Cockpit'))
const NoOrganizationAccess = lazy(() => import('../../shared/pages/NoOrganizationAccess'))
// const Signals = lazy(() => import('../Dashboards/LineManagement/components/Signals'))
// const EquipmentManagement = lazy(() => import('../Dashboards/EquipmentManagement'))
const EquipmentDashboard = lazy(() => import('../Dashboards/EquipmentDashboard'))
const Incidents = lazy(() => import('../Dashboards/Incidents'))
const IncidentDetail = lazy(() => import('../Dashboards/Incidents/IncidentDetail'))
const ProgramSignalThresholdViolationsTable = lazy(
  () =>
    import(
      '../Dashboards/LineManagement/components/programSignalThrehsolds/ProgramSignalThresholdViolationsTable.tsx'
    ),
)

// The retired per-line signals page (AB#5977). The route stays as a permanent
// redirect, not a 404: alert emails, bookmarks and links shared between
// colleagues have pointed here for years, and reshapeLegacyToExplorer carries
// the line, equipment, window and selection across so they land on the same
// data in the Signal Explorer.
const LegacySignalsRoute: FC = (): ReactElement => {
  const {id} = useParams()
  const [searchParams] = useSearchParams()

  return <Navigate replace to={reshapeLegacyToExplorer(id ?? '', searchParams)} />
}

// Organizations are registered by staff only (AB#6862), so the sign-up
// completion form is gone. Auth's SSO callback and old links still point here,
// so the path stays and hands over to the no-organization page, query and all.
export const LegacyCreateOrganizationRoute: FC = (): ReactElement => {
  const {search} = useLocation()

  return <Navigate replace to={`/no-organization${search}`} />
}

// The old comments page became the plant logbook; old links and bookmarks keep
// working, filters included.
export const LegacyLogbookRoute: FC = (): ReactElement => {
  const {search} = useLocation()

  return <Navigate replace to={`/logbook${search}`} />
}

const Router: FC = (): ReactElement => {
  const auth = localStorage.getItem('token') ? localStorage.getItem('token') : null
  const userContext = useUserContext()
  const loadingUser = userContext?.loadingUser ?? true
  const v2App = useV2App()
  const preferredHomePath = v2App
    ? '/home'
    : getPreferredHomeRoute(userContext?.currentUser?.userprofile?.home_view_mode)
  const {userIsLoggedIn} = useAuthContext() as AuthContextProps
  const theme = useTheme()
  // This component is used to scroll to the top of the page when the URL changes
  const ScrollToTop = (): null => {
    const {pathname} = useLocation()

    useEffect(() => {
      window.scrollTo(0, 0)
    }, [pathname])

    return null
  }

  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  // With the new experience on, its shell (V2App) takes every width, phones
  // included (AB#6639): today's mobile layout below is only for everyone else.
  const v1Mobile = isMobile && !v2App
  // The dashboard print route (AB#6645) is rendered by a headless browser on a
  // read-only session: no usage events, no Birdie recording.
  const {pathname} = useLocation()
  const isPrintRoute = isDashboardPrintPath(pathname)
  // The shop-floor display (AB#6756) runs on a screen credential, not a person:
  // no usage events or Birdie there either, and no app shell on any of its pages.
  const isDisplayRoute = isDisplayPath(pathname)
  const isChromeless = isPrintRoute || isDisplayRoute || isDisplayPairPath(pathname)
  // The boot screen (index.html, AB#6639) stays over the app until its first
  // frame: the new shell ends it itself; here everything else (login, today's
  // shell and phones, print, callbacks and the recorder page).
  const v2Shell =
    Boolean(userIsLoggedIn) &&
    v2App &&
    !isChromeless &&
    !OUTSIDE_SHELL_PATHS.some((path) => pathname.startsWith(path))
  useEffect(() => {
    if (!loadingUser && !v2Shell) hideBoot()
  }, [loadingUser, v2Shell])

  // A browser holding a screen credential is that paired screen (AB#6756) and
  // stays on its dashboard, even when a person signs in on it: a shop-floor TV
  // must never become someone's open account. To use the browser otherwise,
  // revoke the screen; its next request then clears the credential.
  if (!isDisplayRoute && readDisplayCredential()) {
    return <Navigate to={DISPLAY_ROUTE} replace />
  }

  return (
    <StrictMode>
      {!isPrintRoute && !isDisplayRoute && <GlobalUsageTracker />}
      {!isPrintRoute && !isDisplayRoute && <BirdieInitializer />}
      <ScrollToTop />
      <EdgeViewCleanup />
      <Routes>
        <Route
          path="/authorize-callback"
          element={
            <Suspense fallback={<DashTemplateLoader />}>
              <AuthCodeCallback />
            </Suspense>
          }
        />
        <Route
          path="/authentication/code/callback/"
          element={
            <Suspense fallback={<DashTemplateLoader />}>
              <AuthCodeCallback />
            </Suspense>
          }
        />
        {/*
          Where Microsoft returns the Entra admin after granting admin consent
          to the Alpinist SharePoint reader app, or after the one-time sign-in
          that grants it a site. Matched before the authenticated /
          unauthenticated branching so neither the auth guard nor the `path="*"`
          catch-alls can swallow the `code`/`state` query params Azure appends.
        */}
        <Route
          path="/alpinist/sharepoint-callback"
          element={
            <Suspense fallback={<DashTemplateLoader />}>
              <SharePointEntraCallback />
            </Suspense>
          }
        />
        {/*
          Public Birdie recording landing page. Birdie's recorder hands control
          back to `/recorder?birdie-recording=…&birdie-embed=1`; this route is
          matched regardless of auth state and BEFORE the authenticated /
          unauthenticated branching below, so neither the auth guard nor the
          `path="*"` catch-alls intercept it (and the index `<Navigate>` routes
          can't strip the query params). The page is intentionally blank and
          must not redirect — see BirdieRecorderPage.
        */}
        <Route path="/recorder" element={<BirdieRecorderPage />} />
        {/*
          The dashboard print route (AB#6645): outside DashTemplate, so no
          drawer, app bar, chat or anything else that writes. Registered for any
          stored token, on desktop and mobile alike, ahead of the loading
          placeholder; the page waits for the user itself.
        */}
        {auth || userIsLoggedIn ? (
          <Route
            path={DASHBOARD_PRINT_ROUTE}
            element={
              <Suspense fallback={null}>
                <DashboardPrintView />
              </Suspense>
            }
          />
        ) : null}
        {/*
          The shop-floor display (AB#6756): the screen's pages work without a
          signed-in person, so they are registered whatever the auth state and
          ahead of the catch-alls. The approver's page needs a person: signed
          out, it sends them to sign in and back (AuthRedirect keeps the URL,
          code included).
        */}
        <Route
          path={DISPLAY_ROUTE}
          element={
            <Suspense fallback={null}>
              <DisplayPairingScreen />
            </Suspense>
          }
        />
        <Route path={DISPLAY_SHORT_ROUTE} element={<Navigate to={DISPLAY_ROUTE} replace />} />
        <Route
          path={DISPLAY_DASHBOARD_ROUTE}
          element={
            <Suspense fallback={null}>
              <DisplayDashboardView />
            </Suspense>
          }
        />
        <Route
          path={DISPLAY_PAIR_ROUTE}
          element={
            loadingUser && (auth || userIsLoggedIn) ? (
              <DashTemplateLoader />
            ) : userIsLoggedIn ? (
              <Suspense fallback={<DashTemplateLoader />}>
                <DisplayPairPage />
              </Suspense>
            ) : isNewAuthSystemEnabled ? (
              <Suspense fallback={<DashTemplateLoader />}>
                <AuthRedirect />
              </Suspense>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Routes for unauthenticated users based on new auth system flag */}
        {!userIsLoggedIn && !loadingUser ? (
          isNewAuthSystemEnabled ? (
            <>
              <Route
                path="/login"
                element={
                  <Suspense fallback={<AuthTemplateLoader />}>
                    <AuthRedirect />
                  </Suspense>
                }
              />
              <Route
                path="/create-account"
                element={
                  <Suspense fallback={<AuthTemplateLoader />}>
                    <AuthRedirect />
                  </Suspense>
                }
              />
              <Route
                path="*"
                element={
                  <Suspense fallback={<DashTemplateLoader />}>
                    <AuthRedirect />
                  </Suspense>
                }
              />
            </>
          ) : (
            <Route
              path="/"
              element={
                <Suspense fallback={<AuthTemplateLoader />}>
                  <AuthTemplate />
                </Suspense>
              }
            >
              <Route path="login" element={<LoginForm />} />
              <Route path="create-account" element={<RegisterForm />} />
            </Route>
          )
        ) : null}
        {/* Routes for authenticated users */}

        {userIsLoggedIn && !loadingUser && v1Mobile && (
          <Route
            path="/"
            element={
              <Suspense fallback={<DashTemplateLoader />}>
                <DashTemplate isMobile={isMobile} />
              </Suspense>
            }
          >
            <Route index element={<Navigate to="agent-chat" replace />} />
            <Route
              path="incidents"
              element={
                <Suspense fallback={<DashboardManagementLoader />}>
                  <TimeIntervalProvider initialTimeRange={INITIAL_TIME_RANGE_ONE_DAY}>
                    <Incidents />
                  </TimeIntervalProvider>
                </Suspense>
              }
            />
            <Route
              path="incidents/:incidentId"
              element={
                <Suspense fallback={<DashboardManagementLoader />}>
                  <IncidentDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboards"
              element={
                <Suspense fallback={<DashboardManagementLoader />}>
                  <CustomDashboards />
                </Suspense>
              }
            />
            <Route
              path="dashboards/:dashboardId"
              element={
                <Suspense fallback={<DashboardManagementLoader />}>
                  <CustomDashboardView />
                </Suspense>
              }
            />
            <Route
              path="home"
              element={
                <Suspense fallback={<DashboardManagementLoader />}>
                  <Home />
                </Suspense>
              }
            />
            <Route
              path="lines_overview"
              element={
                <Suspense fallback={<DashboardManagementLoader />}>
                  <LinesOverview />
                </Suspense>
              }
            />
            <Route
              path="cockpit"
              element={
                <Suspense fallback={<DashboardManagementLoader />}>
                  <BasicDashboardContainer>
                    <Cockpit />
                  </BasicDashboardContainer>
                </Suspense>
              }
            />
            <Route path="agent-chat" element={<AgentChat isMobile={isMobile} />} />
            <Route
              path="signal-explorer"
              element={
                <Suspense fallback={<DashboardManagementLoader />}>
                  <SignalsWorkspace />
                </Suspense>
              }
            />
            {/* Registered on mobile as well as desktop: alert emails are read
                on phones, and the retired page they point at was desktop-only,
                so those links used to dead-end here. */}
            <Route path="line/:id/signals" element={<LegacySignalsRoute />} />
            <Route
              path="*"
              element={
                <Suspense fallback={<DashTemplateLoader />}>
                  <DesktopOnly />
                </Suspense>
              }
            />
          </Route>
        )}

        {loadingUser && (auth || userIsLoggedIn) ? (
          <Route path="/*" element={<DashTemplateLoader />} />
        ) : userIsLoggedIn && !loadingUser && !v1Mobile ? (
          <>
            {/* The root switch (AB#6639): with the new experience on, its shell
                (V2App) takes every signed-in page at every width; off or
                switched back to classic, today's shell, unchanged. */}
            {v2App ? (
              <>
                {/* "/" too: a bare "/*" ranks below the minimized template's
                    "/" layout below, which would take the root. */}
                <Route
                  path="/"
                  element={
                    <Suspense fallback={null}>
                      <V2App />
                    </Suspense>
                  }
                />
                <Route
                  path="/*"
                  element={
                    <Suspense fallback={null}>
                      <V2App />
                    </Suspense>
                  }
                />
              </>
            ) : (
              <Route
                path="/"
                element={
                  <Suspense fallback={<DashTemplateLoader />}>
                    <DashTemplate isMobile={isMobile} />
                  </Suspense>
                }
              >
                <Route index element={<Navigate to={preferredHomePath} replace />} />
                <Route
                  path="connections"
                  element={
                    <Navigate
                      to="/settings?sub_menu=machine_admin&tab=connections&edge_tab=edge_device"
                      replace
                    />
                  }
                />
                <Route
                  path="/connections/edge-native-releases"
                  element={
                    <Navigate
                      to="/settings?sub_menu=machine_admin&tab=connections&edge_tab=edge_native&edge_view=edge_native_releases"
                      replace
                    />
                  }
                />
                <Route
                  path="anomalies"
                  element={
                    <Suspense fallback={<DashboardManagementLoader />}>
                      <AnomalyComponent />
                    </Suspense>
                  }
                />
                <Route
                  path="incidents"
                  element={
                    <Suspense fallback={<DashboardManagementLoader />}>
                      <TimeIntervalProvider initialTimeRange={INITIAL_TIME_RANGE_ONE_DAY}>
                        <Incidents />
                      </TimeIntervalProvider>
                    </Suspense>
                  }
                />
                <Route
                  path="incidents/:incidentId"
                  element={
                    <Suspense fallback={<DashboardManagementLoader />}>
                      <IncidentDetail />
                    </Suspense>
                  }
                />
                <Route
                  path="dashboards"
                  element={
                    <Suspense fallback={<DashboardManagementLoader />}>
                      <CustomDashboards />
                    </Suspense>
                  }
                />
                <Route
                  path="dashboards/:dashboardId"
                  element={
                    <Suspense fallback={<DashboardManagementLoader />}>
                      <CustomDashboardView />
                    </Suspense>
                  }
                />
                <Route
                  path="logbook"
                  element={
                    <Suspense fallback={<DashboardManagementLoader />}>
                      <LogbookEntryComponent />
                    </Suspense>
                  }
                />
                <Route path="comments" element={<LegacyLogbookRoute />} />
                <Route
                  path="external_bookmarks"
                  element={
                    <Suspense fallback={<DashboardManagementLoader />}>
                      <ExternalBookmarkComponent />
                    </Suspense>
                  }
                />
                <Route
                  path="cockpit"
                  element={
                    <Suspense fallback={<DashboardManagementLoader />}>
                      <BasicDashboardContainer>
                        <Cockpit />
                      </BasicDashboardContainer>
                    </Suspense>
                  }
                />
                <Route
                  path="threshold-violations"
                  element={
                    <Suspense fallback={<DashboardManagementLoader />}>
                      <ProgramSignalThresholdViolationsTable />
                    </Suspense>
                  }
                />
                <Route
                  path="signal-value-trackers"
                  element={
                    <Suspense fallback={<DashboardManagementLoader />}>
                      <TimeIntervalProvider initialTimeRange={INITIAL_TIME_RANGE_ONE_DAY}>
                        <SignalValueTrackersTable />
                      </TimeIntervalProvider>
                    </Suspense>
                  }
                />
                <Route path="create-organization" element={<LegacyCreateOrganizationRoute />} />
                <Route path="no-organization" element={<NoOrganizationAccess />} />
                <Route
                  path="home"
                  element={
                    <Suspense fallback={<DashboardManagementLoader />}>
                      <Home />
                    </Suspense>
                  }
                />
                <Route
                  path="lines_overview"
                  element={
                    <Suspense fallback={<LinesOverviewLoader />}>
                      <LinesOverview />
                    </Suspense>
                  }
                />
                <Route
                  path="line/:id"
                  element={
                    <Suspense fallback={<LineDashboardLoader />}>
                      <LineDashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="equipment/:id"
                  element={
                    <Suspense fallback={<LineDashboardLoader />}>
                      <EquipmentDashboard />
                    </Suspense>
                  }
                />
                {/* <Route
								path="equipment/:id/equipment_management"
								element={<Suspense fallback={<LineDashboardLoader />}><EquipmentManagement /></Suspense>}
							/> */}
                <Route path="line/:id/signals" element={<LegacySignalsRoute />} />
                {/* <Route
								path="line/:id/line_management"
								element={<Suspense fallback={<LineManagementLoader />}><LineManagement /></Suspense>}
							/> */}
                <Route path="maintenance_calendar/" element={<LineMaintenance />} />
                <Route
                  path="settings"
                  element={
                    <Suspense fallback={<SettingsLoader />}>
                      <Settings />
                    </Suspense>
                  }
                />
                <Route path="/inactive_user" element={<InactiveUser />} />
                <Route
                  path="batch_traceability"
                  element={
                    <Suspense fallback={<BatchTraceabilityLoader />}>
                      <BatchTraceability />
                    </Suspense>
                  }
                />
                {/* Reports live in the main nav, not under Settings (AB#5828). */}
                <Route
                  path="reports"
                  element={
                    <Suspense fallback={<TasksLoader />}>
                      <Reports />
                    </Suspense>
                  }
                />
                <Route
                  path="tasks"
                  element={
                    <Suspense fallback={<TasksLoader />}>
                      <Tasks />
                    </Suspense>
                  }
                />
                {/* Automations moved into Settings → Machine Admin (AB#5699).
                  Keep the old path working for existing links and bookmarks. */}
                <Route
                  path="automations"
                  element={
                    <Navigate replace to="/settings?sub_menu=machine_admin&tab=automations" />
                  }
                />
                <Route
                  path="signal-explorer"
                  element={
                    <Suspense fallback={<DashboardManagementLoader />}>
                      <SignalsWorkspace />
                    </Suspense>
                  }
                />
                <Route path="*" element={<NoPage />} />
              </Route>
            )}
            <Route
              path="/"
              element={
                <Suspense fallback={<MinimizedTemplateLoader />}>
                  <MinimizedTemplate />
                </Suspense>
              }
            >
              <Route path="startup-kit-onboarding" element={<StartupKitOnboarding />} />
              <Route
                path="connections/add"
                element={
                  <Navigate
                    to="/settings?sub_menu=machine_admin&tab=connections&edge_tab=edge_device"
                    replace
                  />
                }
              />
            </Route>
            <Route path="welcome" element={<Welcome />} />
          </>
        ) : null}
        {/* Additional routes for other sections */}
        {!loadingUser && !v1Mobile && (
          <Route
            path="/"
            element={
              <Suspense fallback={<MinimizedTemplateLoader />}>
                <MinimizedTemplate />
              </Suspense>
            }
          >
            <Route path="dashboard/:id" element={<SignalsDashboard />} />
            {/* Shadowed by today's shell's own /cockpit; V2App's "/*" ranks
                lower than this, so it is left out with the new experience. */}
            {!v2App && <Route path="cockpit" element={<Cockpit />} />}
          </Route>
        )}
        {!v1Mobile && (
          <Route
            path="/"
            element={
              <Suspense fallback={<MinimizedTemplateLoader />}>
                <MinimizedTemplate />
              </Suspense>
            }
          >
            <Route path="compare-pdfs" element={<ComparePDFs />} />
          </Route>
        )}
      </Routes>
    </StrictMode>
  )
}

export default Router
